import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Close, ExpandView, Info } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useElementHeight } from "@/hooks/useElementHeight";
import { cn } from "@/utils/utils";
import { TrendLayoutContext } from "./trendLayout";

export interface KPICardPopover {
  title: string;
  /** Tailwind width class for the panel. */
  widthClassName: string;
  content: React.ReactNode;
}

interface KPICardProps {
  title: string;
  info?: string;
  /** The breakdown opened from the card's expand button; the button is absent without one. */
  popover?: KPICardPopover;
  className?: string;
  dataTest: string;
  children: React.ReactNode;
}

// The card grows past this when its figures wrap; the loading skeleton matches it.
const CARD_MIN_HEIGHT_PX = 124;

const KPICard = ({
  title,
  info,
  popover,
  className,
  dataTest,
  children
}: KPICardProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { ref: cardRef, height: measuredHeight } =
    useElementHeight<HTMLDivElement>();
  const cardHeight = measuredHeight || CARD_MIN_HEIGHT_PX;
  const [trendFits, setTrendFits] = useState<Record<string, boolean>>({});
  const report = useCallback(
    (key: string, fits: boolean) =>
      setTrendFits((current) =>
        current[key] === fits ? current : { ...current, [key]: fits }
      ),
    []
  );
  const trendLayout = useMemo(
    () => ({ report, allFit: Object.values(trendFits).every(Boolean) }),
    [report, trendFits]
  );

  const card = (
    <div
      ref={cardRef}
      data-test={dataTest}
      className={cn(
        "group flex min-w-0 flex-col gap-4 [container-type:inline-size] rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card py-3 pl-4 pr-3 transition-colors animate-fadeIn hover:border-border-border2 hover:bg-surface-widget-card-hover",
        className
      )}
      style={{ minHeight: CARD_MIN_HEIGHT_PX }}
    >
      <div className="flex h-6 items-center gap-1">
        <div className="flex min-w-0 items-center">
          <Text
            as="div"
            data-test={`${dataTest}-title`}
            variant="bodySecondary"
            className="truncate text-sm leading-5 [@container(min-width:170px)]:text-base"
          >
            {title}
          </Text>
          {info && (
            <Tooltip delayDuration={0}>
              {/* Zero-width until shown, so the hidden icon never takes room from the title. */}
              <TooltipTrigger
                data-test={`${dataTest}-info`}
                aria-label={info}
                className={cn(
                  "w-0 shrink-0 overflow-hidden opacity-0 transition-opacity",
                  "group-hover:w-auto group-hover:opacity-100",
                  "focus-visible:w-auto focus-visible:opacity-100",
                  "data-[state=delayed-open]:w-auto data-[state=delayed-open]:opacity-100",
                  "data-[state=instant-open]:w-auto data-[state=instant-open]:opacity-100"
                )}
              >
                <IconContainer
                  as="span"
                  size="sm"
                  color="tertiary"
                  className="ml-1"
                >
                  <Info />
                </IconContainer>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={20} enableMarkdown>
                {info}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {popover && (
          <PopoverTrigger asChild>
            <IconButton
              data-test={`${dataTest}-expand`}
              aria-label={t("kpisV2.openBreakdown", { title })}
              variant="icon"
              iconColor="secondary"
              size="sm"
              className="ml-auto opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            >
              <ExpandView />
            </IconButton>
          </PopoverTrigger>
        )}
      </div>
      <div className="flex min-w-0 gap-4 [@container(min-width:320px)]:gap-8">
        <TrendLayoutContext.Provider value={trendLayout}>
          {children}
        </TrendLayoutContext.Provider>
      </div>
    </div>
  );

  if (!popover) return card;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>{card}</PopoverAnchor>
      <PopoverContent
        data-test={`${dataTest}-popover`}
        align="end"
        side="bottom"
        // The breakdown opens over the card it expands, so it is offset up by the
        // card's measured height, which grows with its content. It is at least as
        // wide as the card and right-aligned with it, so a wider one grows leftwards
        // and its close button always lands on the card's expand button.
        sideOffset={-cardHeight}
        padding={0}
        className={cn(
          "min-w-[var(--radix-popover-trigger-width)] rounded-[16px]",
          popover.widthClassName
        )}
      >
        <div className="flex items-center justify-between border-b border-border-border1 py-2.5 pl-4 pr-2.5">
          <div className="flex items-baseline gap-2">
            <Text
              as="div"
              variant="body"
              className="text-[18px] font-medium leading-6 text-h1"
            >
              {popover.title}
            </Text>
            <Text as="span" variant="smBodySecondary">
              {t("timeframes.ytd")}
            </Text>
          </div>
          <IconButton
            aria-label={t("kpisV2.close")}
            variant="iconNoBg"
            iconColor="secondary"
            size="md"
            onClick={() => setIsOpen(false)}
          >
            <Close />
          </IconButton>
        </div>
        <div className="flex flex-col gap-4 p-4">{popover.content}</div>
      </PopoverContent>
    </Popover>
  );
};

export default KPICard;
